import { useState, useCallback } from "react";
import { useScanner } from "../contexts/ScannerContext";
import { useWbSettings } from "../lib/useWbSettings";
import { removeDarkBackground } from "../lib/imageProcessing";
import { heatColor, generateHeatmap } from "../lib/heatmap";

// ─── Mock foot (dark background — realistic Plustek output) ──────────────────

function generateMockFoot() {
  const W = 1400, H = 900;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, W, H);

  const drawFoot = (cx, cy, scale, flip) => {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(flip, 1);
    const s = scale;

    const skinGrad = ctx.createLinearGradient(-100*s, -200*s, 100*s, 300*s);
    skinGrad.addColorStop(0, "#7a4a2e");
    skinGrad.addColorStop(0.4, "#8a5535");
    skinGrad.addColorStop(0.7, "#9a6040");
    skinGrad.addColorStop(1, "#6a3d22");
    ctx.fillStyle = skinGrad;
    ctx.beginPath();
    ctx.ellipse(0, 290*s, 96*s, 123*s, 0, 0, Math.PI*2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-97*s, 270*s);
    ctx.bezierCurveTo(-104*s, 80*s, -83*s, -53*s, -57*s, -193*s);
    ctx.lineTo(71*s, -193*s);
    ctx.bezierCurveTo(91*s, -53*s, 97*s, 80*s, 77*s, 270*s);
    ctx.closePath();
    ctx.fill();

    // Heel — high pressure
    const heelG = ctx.createRadialGradient(2*s, 285*s, 5*s, 2*s, 285*s, 85*s);
    heelG.addColorStop(0, "rgba(195,145,100,0.95)");
    heelG.addColorStop(0.5, "rgba(160,110,70,0.6)");
    heelG.addColorStop(1, "rgba(120,75,45,0)");
    ctx.fillStyle = heelG;
    ctx.beginPath(); ctx.ellipse(2*s, 285*s, 83*s, 105*s, 0, 0, Math.PI*2); ctx.fill();

    // Ball — high pressure
    const ballG = ctx.createRadialGradient(-5*s, -125*s, 8*s, -5*s, -125*s, 82*s);
    ballG.addColorStop(0, "rgba(190,140,95,0.9)");
    ballG.addColorStop(0.5, "rgba(155,108,68,0.55)");
    ballG.addColorStop(1, "rgba(120,78,48,0)");
    ctx.fillStyle = ballG;
    ctx.beginPath(); ctx.ellipse(-5*s, -125*s, 85*s, 72*s, 0, 0, Math.PI*2); ctx.fill();

    // Outer met
    const outerG = ctx.createRadialGradient(58*s, -95*s, 4*s, 58*s, -95*s, 48*s);
    outerG.addColorStop(0, "rgba(175,128,88,0.8)");
    outerG.addColorStop(1, "rgba(120,78,48,0)");
    ctx.fillStyle = outerG;
    ctx.beginPath(); ctx.ellipse(58*s, -95*s, 44*s, 52*s, 0, 0, Math.PI*2); ctx.fill();

    // Arch — dark (no contact)
    const archG = ctx.createRadialGradient(-18*s, 95*s, 5*s, -18*s, 95*s, 88*s);
    archG.addColorStop(0, "rgba(15,8,3,0.85)");
    archG.addColorStop(0.6, "rgba(20,12,6,0.5)");
    archG.addColorStop(1, "rgba(20,12,6,0)");
    ctx.fillStyle = archG;
    ctx.beginPath(); ctx.ellipse(-18*s, 95*s, 82*s, 115*s, 0, 0, Math.PI*2); ctx.fill();

    // Toes
    [[-63,-222,28,36],[-27,-242,21,31],[4,-242,19,29],[33,-232,17,26],[60,-212,14,21]].forEach(([tx,ty,rx,ry]) => {
      ctx.fillStyle = "#7a4a2e";
      ctx.beginPath(); ctx.ellipse(tx*s, ty*s, rx*s, ry*s, 0, 0, Math.PI*2); ctx.fill();
      const tg = ctx.createRadialGradient(tx*s, ty*s+ry*s*0.15, 2, tx*s, ty*s+ry*s*0.15, rx*s*0.85);
      tg.addColorStop(0, "rgba(185,135,90,0.82)");
      tg.addColorStop(1, "rgba(120,75,45,0)");
      ctx.fillStyle = tg;
      ctx.beginPath(); ctx.ellipse(tx*s, ty*s, rx*s, ry*s, 0, 0, Math.PI*2); ctx.fill();
    });
    ctx.restore();
  };

  drawFoot(415, 448, 0.74, 1);
  drawFoot(985, 448, 0.74, -1);

  const imgData = ctx.getImageData(0, 0, W, H);
  const px = imgData.data;
  for (let i = 0; i < px.length; i += 4) {
    const n = (Math.random() - 0.5) * 6;
    px[i]   = Math.max(0, Math.min(255, px[i]+n));
    px[i+1] = Math.max(0, Math.min(255, px[i+1]+n));
    px[i+2] = Math.max(0, Math.min(255, px[i+2]+n));
  }
  ctx.putImageData(imgData, 0, 0);
  return c.toDataURL("image/jpeg", 0.93);
}

// ─── Presets ──────────────────────────────────────────────────────────────────
// All heatmaps: белый фон, invertMapping=false (красный = макс. контакт).
// Two-step ("wb_heat"): сначала удаляем тёмный фон, потом накладываем карту на белом.

const PRESETS = [
  {
    id: "original",
    name: "Оригинал",
    icon: "📷",
    desc: "Без изменений",
    scanOverride: {},
    pp: null,
  },
  {
    id: "wb_saved",
    name: "Белый фон",
    icon: "⬜",
    desc: "Постобработка из Настроек",
    scanOverride: {},
    pp: { type: "wb_saved" },
  },
  // Тепловые карты — белый фон, красный = максимальное давление
  {
    id: "heat_classic",
    name: "Тепловая: классика",
    icon: "🌡",
    desc: "Синий→зелёный→красный, белый фон",
    scanOverride: {},
    pp: { type: "heatmap", outputBg: "white", palette: "classic", opacity: 0.85, invertMapping: false },
  },
  {
    id: "heat_classic_dark",
    name: "Тепловая: тёмный",
    icon: "🌡",
    desc: "Синий→зелёный→красный, тёмный фон",
    scanOverride: {},
    pp: { type: "heatmap", outputBg: "dark", palette: "classic", opacity: 0.9, invertMapping: false },
  },
  {
    id: "heat_hot",
    name: "Тепловая: горячая",
    icon: "🔥",
    desc: "Чёрный→красный→жёлтый, белый фон",
    scanOverride: {},
    pp: { type: "heatmap", outputBg: "white", palette: "hot", opacity: 0.9, invertMapping: false },
  },
  {
    id: "heat_hot_dark",
    name: "Hot: тёмный фон",
    icon: "♨️",
    desc: "Горячая палитра, тёмный фон",
    scanOverride: {},
    pp: { type: "heatmap", outputBg: "dark", palette: "hot", opacity: 0.95, invertMapping: false },
  },
  // Двухшаговый: сначала WB-удаление фона → потом тепловая карта на чистом изображении
  {
    id: "heat_wb_classic",
    name: "WB + тепловая",
    icon: "✨",
    desc: "Сначала белый фон → потом карта давления",
    scanOverride: {},
    pp: { type: "wb_heat", palette: "classic", opacity: 0.85, invertMapping: false },
  },
  {
    id: "heat_manual",
    name: "Ручная тепловая",
    icon: "🎛",
    desc: "Настройки из панели справа",
    scanOverride: {},
    pp: { type: "heatmap", manual: true },
  },
];

// Two-step: apply WB removal first, then heatmap detecting near-white as background
async function wbThenHeatmap(rawImg, wbSettings, heatCfg) {
  const wbImg = await removeDarkBackground(rawImg, wbSettings);
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth, H = img.naturalHeight;
      const src = document.createElement("canvas");
      src.width = W; src.height = H;
      const sctx = src.getContext("2d");
      sctx.drawImage(img, 0, 0);
      const { data: px } = sctx.getImageData(0, 0, W, H);

      // On white-bg image: near-white = background, darker = foot
      const WHITE_THRESH = 240;
      const lums = new Float32Array(W * H);
      const footLums = [];
      for (let i = 0; i < px.length; i += 4) {
        const lum = px[i] * 0.299 + px[i+1] * 0.587 + px[i+2] * 0.114;
        lums[i / 4] = lum;
        if (lum < WHITE_THRESH) footLums.push(lum);
      }
      // Percentile normalization (5–95%) — same as generateHeatmap
      footLums.sort((a, b) => a - b);
      const p5  = footLums[Math.floor(footLums.length * 0.05)] ?? 0;
      const p95 = footLums[Math.floor(footLums.length * 0.95)] ?? 200;
      const range = Math.max(1, p95 - p5);

      const out = document.createElement("canvas");
      out.width = W; out.height = H;
      const octx = out.getContext("2d");
      octx.fillStyle = "#ffffff";
      octx.fillRect(0, 0, W, H);

      const overlay = document.createElement("canvas");
      overlay.width = W; overlay.height = H;
      const ovctx = overlay.getContext("2d");
      const hd = ovctx.createImageData(W, H);
      const hp = hd.data;

      const { opacity = 0.85, invertMapping = false, palette = "classic" } = heatCfg;
      for (let i = 0; i < px.length; i += 4) {
        const lum = lums[i / 4];
        if (lum >= WHITE_THRESH) { hp[i+3] = 0; continue; }
        // On white-bg image: DARKER pixel = more contact.
        // Map: dark = high pressure = t=1 = RED
        let t = (p95 - lum) / range; // invert: darker → higher t
        if (invertMapping) t = 1 - t;
        t = Math.max(0, Math.min(1, t));
        const [r, g, b] = heatColor(t, palette);
        hp[i] = r; hp[i+1] = g; hp[i+2] = b; hp[i+3] = Math.round(opacity * 255);
      }
      ovctx.putImageData(hd, 0, 0);
      octx.drawImage(overlay, 0, 0);
      resolve(out.toDataURL("image/jpeg", 0.92));
    };
    img.src = wbImg;
  });
}


async function applyPP(rawImg, pp, heatSettings, wbSettings) {
  if (!pp) return rawImg;

  if (pp.type === "wb_saved") {
    return removeDarkBackground(rawImg, wbSettings);
  }
  if (pp.type === "heatmap") {
    const cfg = pp.manual
      ? heatSettings
      : {
          opacity: pp.opacity ?? 0.85,
          bgThreshold: heatSettings.bgThreshold,   // always from controls
          invertMapping: heatSettings.invertMapping, // always from controls
          outputBg: pp.outputBg ?? "white",
          palette: pp.palette ?? "classic",
        };
    return generateHeatmap(rawImg, cfg);
  }
  if (pp.type === "wb_heat") {
    return wbThenHeatmap(rawImg, wbSettings, {
      opacity: pp.opacity ?? 0.85,
      palette: pp.palette ?? "classic",
      invertMapping: heatSettings.invertMapping, // always from controls
    });
  }
  return rawImg;
}

// ─── Component ────────────────────────────────────────────────────────────────

let nextId = 1;

export default function ScanLabPage() {
  const { scan, scanning, status } = useScanner();
  const [wbSettings] = useWbSettings();

  const [presetId, setPresetId] = useState("heat_classic");
  const [scans, setScans] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [runningAll, setRunningAll] = useState(false);
  const [runProgress, setRunProgress] = useState(null);

  // Manual heatmap controls
  const [heatOpacity, setHeatOpacity] = useState(0.85);
  const [heatBgThreshold, setHeatBgThreshold] = useState(25);
  const [heatInvert, setHeatInvert] = useState(false);
  const [heatOutputBg, setHeatOutputBg] = useState("white");
  const [heatPalette, setHeatPalette] = useState("classic");

  const [log, setLog] = useState([]);
  const addLog = msg => setLog(prev => [`${new Date().toLocaleTimeString("ru-RU")} — ${msg}`, ...prev.slice(0, 29)]);

  const preset = PRESETS.find(p => p.id === presetId) || PRESETS[0];

  const heatSettings = {
    opacity: heatOpacity,
    bgThreshold: heatBgThreshold,
    invertMapping: heatInvert,
    outputBg: heatOutputBg,
    palette: heatPalette,
  };

  const addCard = useCallback((p, img, extraDesc) => {
    setScans(prev => [...prev, {
      id: nextId++,
      presetId: p.id,
      presetName: p.name,
      presetIcon: p.icon,
      presetDesc: extraDesc || p.desc,
      img,
      ts: new Date().toLocaleTimeString("ru-RU"),
    }]);
  }, []);

  const doMock = useCallback(async () => {
    addLog(`Тест: "${preset.name}"...`);
    setProcessing(true);
    const raw = generateMockFoot();
    const out = await applyPP(raw, preset.pp, heatSettings, wbSettings);
    addCard(preset, out);
    addLog(`✓ "${preset.name}" добавлен`);
    setProcessing(false);
  }, [preset, heatSettings, wbSettings, addCard]);

  const doScan = useCallback(async () => {
    addLog(`Сканирование: "${preset.name}"...`);
    setProcessing(true);
    await scan(async (pages) => {
      if (!pages.length) return;
      const out = await applyPP(pages[0].base64, preset.pp, heatSettings, wbSettings);
      addCard(preset, out);
      addLog(`✓ "${preset.name}" добавлен`);
    }, preset.scanOverride ?? {});
    setProcessing(false);
  }, [scan, preset, heatSettings, wbSettings, addCard]);

  const doAllMock = useCallback(async () => {
    addLog("Генерирую все варианты из одного мока...");
    setRunningAll(true);
    const raw = generateMockFoot();
    for (let i = 0; i < PRESETS.length; i++) {
      const p = PRESETS[i];
      setRunProgress(`${i+1}/${PRESETS.length}`);
      const out = await applyPP(raw, p.pp, heatSettings, wbSettings);
      addCard(p, out);
    }
    setRunProgress(null);
    setRunningAll(false);
    addLog("✓ Все варианты готовы");
  }, [heatSettings, wbSettings, addCard]);

  const doAllScan = useCallback(async () => {
    addLog("Один скан → все варианты...");
    setRunningAll(true);
    await scan(async (pages) => {
      if (!pages.length) return;
      const raw = pages[0].base64;
      for (let i = 0; i < PRESETS.length; i++) {
        const p = PRESETS[i];
        setRunProgress(`${i+1}/${PRESETS.length}`);
        const out = await applyPP(raw, p.pp, heatSettings, wbSettings);
        addCard(p, out);
      }
      addLog("✓ Все варианты готовы");
    }, {});
    setRunProgress(null);
    setRunningAll(false);
  }, [scan, heatSettings, wbSettings, addCard]);

  const applyManualHeat = useCallback(async () => {
    const p = PRESETS.find(x => x.id === "heat_manual");
    const desc = `opacity ${Math.round(heatOpacity*100)}% · lum ${heatBgThreshold} · ${heatPalette} · ${heatOutputBg}${heatInvert ? " · инв." : ""}`;
    addLog(`Ручная тепловая: ${desc}...`);
    setProcessing(true);
    const raw = generateMockFoot();
    const out = await generateHeatmap(raw, heatSettings);
    addCard(p, out, desc);
    setProcessing(false);
    addLog("✓ Добавлено");
  }, [heatSettings, addCard]);

  const deleteScan = id => setScans(prev => prev.filter(s => s.id !== id));
  const clearAll = () => { setScans([]); addLog("Коллекция очищена"); };

  const busy = scanning || processing || runningAll;

  return (
    <div className="lab-page">
      {/* Header */}
      <div className="lab-header">
        <div>
          <h2 className="lab-title">🔬 Лаборатория · Тепловая карта</h2>
          <p className="lab-subtitle">{scans.length} результатов · Сравнение режимов</p>
        </div>
        <div className="lab-actions">
          <span className={`lab-status lab-status-${status}`}>
            {status === "connected" ? "● Сканер" : "○ Нет сканера"}
          </span>
          {scans.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={clearAll} disabled={busy}>🗑 Очистить</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={doMock} disabled={busy}>🗂 Тест</button>
          <button className="btn btn-ghost btn-sm" onClick={doAllMock} disabled={busy}>
            {runningAll ? `⏳ ${runProgress}…` : "🗂 Все (тест)"}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={doAllScan} disabled={busy || status !== "connected"}>
            {runningAll ? `⏳ ${runProgress}…` : "📷 Все (скан)"}
          </button>
          <button className="btn btn-primary" onClick={doScan} disabled={busy || status !== "connected"}>
            {scanning ? "Сканирование..." : "📷 Сканировать"}
          </button>
        </div>
      </div>

      {/* Preset strip */}
      <div className="lab-presets">
        {PRESETS.map(p => (
          <button
            key={p.id}
            className={`lab-preset-card${presetId === p.id ? " active" : ""}`}
            onClick={() => setPresetId(p.id)}
            disabled={busy}
            title={p.desc}
          >
            <span className="lab-preset-icon">{p.icon}</span>
            <span className="lab-preset-name">{p.name}</span>
            <span className="lab-preset-desc">{p.desc}</span>
          </button>
        ))}
      </div>

      <div className="lab-body">
        {/* Grid */}
        <div className="lab-grid-area">
          {scans.length === 0 ? (
            <div className="lab-grid-empty">
              <div style={{ fontSize: 48, marginBottom: 12 }}>🌡</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Коллекция пуста</div>
              <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.8 }}>
                Нажми <strong>«Все (тест)»</strong> — увидишь все 8 режимов сразу.<br />
                Или выбери пресет и нажми <strong>«Тест»</strong>.<br />
                Для подбора параметров: <strong>«🎛 Ручная тепловая»</strong> + кнопка <strong>«▶ Применить»</strong>.
              </div>
            </div>
          ) : (
            <div className="lab-grid">
              {scans.map(s => (
                <div key={s.id} className="lab-card" onClick={() => setLightbox(s)}>
                  <div className="lab-card-header">
                    <span className="lab-card-icon">{s.presetIcon}</span>
                    <span className="lab-card-name">{s.presetName}</span>
                    <span className="lab-card-ts">{s.ts}</span>
                    <button className="lab-card-del" onClick={e => { e.stopPropagation(); deleteScan(s.id); }}>×</button>
                  </div>
                  <div className="lab-card-img-wrap">
                    <img src={s.img} className="lab-card-img" alt={s.presetName} />
                  </div>
                  <div className="lab-card-desc">{s.presetDesc}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="lab-controls">

          {/* Manual heatmap */}
          <div className="lab-ctrl-section">
            <div className="lab-ctrl-title">🌡 Тепловая карта — ручные</div>
            <div className="lab-ctrl-hint">Пресет «🎛 Ручная тепловая»</div>

            <div className="lab-ctrl-row">
              <label className="lab-ctrl-label">Прозрачность</label>
              <input type="range" min="0.2" max="1" step="0.05" value={heatOpacity}
                onChange={e => setHeatOpacity(Number(e.target.value))} className="lab-slider" />
              <span className="lab-ctrl-val">{Math.round(heatOpacity*100)}%</span>
            </div>

            <div className="lab-ctrl-row">
              <label className="lab-ctrl-label">Lum фона</label>
              <input type="range" min="5" max="80" value={heatBgThreshold}
                onChange={e => setHeatBgThreshold(Number(e.target.value))} className="lab-slider" />
              <span className="lab-ctrl-val">{heatBgThreshold}</span>
            </div>

            <div className="lab-ctrl-row">
              <label className="lab-ctrl-label">Палитра</label>
              <select className="lab-ctrl-select" value={heatPalette} onChange={e => setHeatPalette(e.target.value)}>
                <option value="classic">Классика</option>
                <option value="hot">Горячая</option>
                <option value="cool">Холодная</option>
                <option value="grey">Серая</option>
              </select>
            </div>

            <div className="lab-ctrl-row">
              <label className="lab-ctrl-label">Фон</label>
              <select className="lab-ctrl-select" value={heatOutputBg} onChange={e => setHeatOutputBg(e.target.value)}>
                <option value="dark">Тёмный</option>
                <option value="white">Белый</option>
              </select>
            </div>

            <div className="lab-ctrl-row">
              <label className="lab-ctrl-label">Инверсия</label>
              <label className="lab-toggle">
                <input type="checkbox" checked={heatInvert} onChange={e => setHeatInvert(e.target.checked)} />
                <span className="lab-toggle-track" />
              </label>
            </div>

            <div className="lab-heatmap-legend">
              <span style={{ color: "#44f" }}>● мало</span>
              <span style={{ color: "#0b0" }}>● средне</span>
              <span style={{ color: "#fc0" }}>● много</span>
              <span style={{ color: "#f33" }}>● макс.</span>
            </div>

            <button className="btn btn-primary btn-sm lab-wb-apply" disabled={busy} onClick={applyManualHeat}>
              ▶ Применить (тест)
            </button>
          </div>

          {/* Saved WB settings (read-only display) */}
          <div className="lab-ctrl-section">
            <div className="lab-ctrl-title">⬜ Белый фон (сохранено)</div>
            <div className="lab-ctrl-hint">Из раздела «Настройки»</div>
            <div className="lab-wb-saved-row">
              <span>Lum (порог)</span><strong>{wbSettings.threshold}</strong>
            </div>
            <div className="lab-wb-saved-row">
              <span>Яркость</span><strong>+{wbSettings.brightness}</strong>
            </div>
            <div className="lab-wb-saved-row">
              <span>Контраст</span><strong>+{wbSettings.contrast}</strong>
            </div>
          </div>

          {/* Log */}
          <div className="lab-ctrl-section">
            <div className="lab-ctrl-title">📋 Лог</div>
            <div className="lab-log">
              {log.length === 0
                ? <span className="lab-log-empty">Нет записей</span>
                : log.map((l, i) => <div key={i} className="lab-log-row">{l}</div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="lab-lightbox" onClick={() => setLightbox(null)}>
          <div className="lab-lightbox-window" onClick={e => e.stopPropagation()}>
            <div className="lab-lightbox-header">
              <span>{lightbox.presetIcon} {lightbox.presetName} · {lightbox.presetDesc}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setLightbox(null)}>✕</button>
            </div>
            <div className="lab-lightbox-img-wrap">
              <img src={lightbox.img} className="lab-lightbox-img" alt={lightbox.presetName} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
